import {useState} from "react";
import {Breadcrumb, Button, Col, Form} from "react-bootstrap";
import {Controller, useForm} from "react-hook-form";
import {Link, useNavigate} from "react-router-dom";
import {useLang, usePrivateAPI, useTermList} from "../../hooks";
import {useNotification} from "../../hooks/useNotification";
import {InputValidationError} from "../../libs/error";
import {DatePicker, SelectPicker, TagInput, Toggle, TreePicker} from "rsuite";

import {yupResolver} from "@hookform/resolvers/yup";
import CircleLoader from "../../components/Loaders/CircleLoader";
import Layout from "../../components/layouts/layout";
import {PostInputSchema} from "./schema";
import {createPost} from "../../services/post";
import {ImageInput} from "../../components/MediaInputs/imageInput";
import {BackButton} from "../../components/BackButton/BackButton";
import {TextEdit} from "../../components";
import MediaModal from "../../components/MediaSelectorModal/MediaModal";
import {useMediaSelector} from "../../components/MediaSelectorModal";
import MediaCard from "../MediaManagement/components/MediaCard";
import {usePageList} from "../../hooks/usePageList";
import {organizeData} from "../../helpers";

export function PostCreatePage() {
  const {t} = useLang();
  const [isLoading, setIsLoading] = useState();
  const navigate = useNavigate();
  const {notifyError, notifySuccess} = useNotification();
  const {pageList, isLoading: pageLoading, error: pageErr}
    = usePageList(null, {page: 1, limit: 100});

  const {
    checkValue,
    setCheckValue,
    selectedMedia,
    setSelectedMedia,
    allMediaId,
    setAllMediaId,
    handleRemove,
  } = useMediaSelector();

  const handleMediaAddClick = async () => {

  };

  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
    setError,
  } = useForm({
    resolver: yupResolver(PostInputSchema(t)),
    defaultValues: {
      is_show: false,
    },
  });

  const {
    termList,
    error: termsError,
    isLoading: isTermsLoading,
  } = useTermList(1, {limit: 1000, page: 1});

  if (termsError) {
    notifyError(termsError);
    navigate("/posts/manage");
  }

  const create = async (data) => {
    try {
      setIsLoading(true);
      const postData = { ...data, media_id: checkValue};
      const post = await createPost(postData)
      notifySuccess({
        title: "Post created successfully",
        message: `Post "${post.name}" created!`,
      });
      navigate("/posts/manage");
    } catch (err) {
      if (err instanceof InputValidationError) {
        err.setRHFError(setError);
        notifyError({
          title: "Input validation failed",
          message: err.message,
        });
        return;
      }
      notifyError({
        title: "Failed to create post",
        message: err.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      {(isTermsLoading || isLoading) && <CircleLoader/>}
      <header>
        <div className="d-flex justify-content-between align-items-center">
          <Breadcrumb className="d-flex align-items-center">
            <li className="breadcrumb-item">
              <Link to={`/posts/manage`}> {t("posts_manage_posts")}</Link>
            </li>
            <Breadcrumb.Item active>
              {t("users_create_crateUser")}
            </Breadcrumb.Item>
          </Breadcrumb>
          <BackButton path={-1} text={t("posts_manage_posts")}/>
        </div>
      </header>
      <section id="#post-create">
        <h2>{t("posts_create_createNewPost")}</h2>
        <Form
          onSubmit={handleSubmit(create)}
          className="mt-4 d-flex  flex-column gap-3"
        >
          <div className="form-row">
            <Form.Group as={Col} md={2} controlId="post_category_id">
              <Form.Label className="mark-required">{t("posts_create_category")}</Form.Label>
              {/*<Form.Control {...register("post_category_id")} as="select" custom>*/}
              {/*  <option value={-1}>None</option>*/}
              {/*  {termList.terms.map((trm) => (*/}
              {/*    <option value={trm.id}>{trm.name}</option>*/}
              {/*  ))}*/}
              {/*</Form.Control>*/}
              <Controller render={({field}) => <TreePicker
                loading={isTermsLoading} {...field}
                data={organizeData(termList.terms)}
                block
                searchable={false}
                placeholder="Select Categories"
                defaultExpandAll
              />
              } name="post_category_id" control={control}/>
              {errors.post_category_id?.message && (
                <Form.Text className="text-danger">
                  {errors.post_category_id.message}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group as={Col} controlId="name">
              <Form.Label className="mark-required">{t("posts_create_name")}</Form.Label>
              <Form.Control
                {...register("name")}
                placeholder={t("posts_create_name_plhd")}
                type="text"
              />
              {errors.name?.message && (
                <Form.Text className="text-danger">
                  {errors.name.message}
                </Form.Text>
              )}
            </Form.Group>
          </div>
          <div className="form-row">
            <Form.Group as={Col} lg={2} controlId="post_date">
              <Form.Label className="mark-required">{t("posts_create_postDate")}</Form.Label>
              <Controller
                control={control}
                name="post_date"
                render={({ field: { onChange, onBlur, value, ref }}) => (
                  <div tabIndex={-1} ref={ref}>
                    <DatePicker value={value} onChange={onChange} format="dd-MM-yyyy" />
                  </div>
                )}
              />
              {errors.post_date?.message && (
                <Form.Text className="text-danger">
                {errors.post_date.message}
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group as={Col} controlId="page_id">
              <Form.Label>{t("menu_content_page")}</Form.Label>
              <Controller control={control} render={
                ({field}) =>
                  <SelectPicker onChange={field.onChange} value={field.value} data={pageList?.pages.map(p => ({
                    label: p.title,
                    value: p.id
                  }))} style={{width: "100%"}} />
              } name="page_id" />

              {errors?.page_id?.message && (
                <Form.Text className="text-danger">{errors.page_id.message}</Form.Text>
              )}
            </Form.Group>

            <Form.Group as={Col} controlId="name">
              <Form.Label>{t("post_input_link")}</Form.Label>
              <Form.Control
                {...register("link")}
                placeholder={t("post_input_link")}
                type="url"
              />
              {errors.link?.message && (
                <Form.Text className="text-danger">
                  {errors.link.message}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group as={Col} controlId="is_show">
              <Form.Label className="mark-required">{t("posts_create_visibility")}</Form.Label>
              <Controller
                control={control}
                name="is_show"
                render={({field}) => (
                  <div>
                    <Toggle
                      {...field}
                      size="lg"
                      checkedChildren={<span>Public</span>}
                      unCheckedChildren={<span>Private</span>}
                      className="m-auto"
                    />
                  </div>
                )}
              />
              {(errors.is_show?.message || Array.isArray(errors.meta_tag)) && (
                <Form.Text className="text-danger">
                  {errors?.is_show?.message ??
                    errors.is_show.find(Boolean).message}
                </Form.Text>
              )}
            </Form.Group>
          </div>
          <Form.Group controlId="image">
            <Form.Label>{t("posts_create_image")}</Form.Label>
            <div className="w-100">
              <Controller
                control={control}
                name="image"
                render={({field}) => (
                  <MediaModal selectedMedia={selectedMedia} setSelectedMedia={setSelectedMedia}
                              checkValue={checkValue} setCheckValue={setCheckValue} allMediaId={allMediaId}
                              handleMediaAddClick={handleMediaAddClick}>
                    {
                      selectedMedia?.length > 0 ? (
                        selectedMedia?.map((md) =>
                          <MediaCard key={md.id} media={md} isDelete handleRemove={handleRemove}/>
                        )
                      ): (
                        <div style={{height: "300px", width: "100%"}}>
                          <ImageInput
                            errorMessage={errors.image?.message}
                            onChange={field.onChange}
                            onError={() => {
                              setError("image", {message: "Invalid image file!"});
                            }}
                          />
                        </div>
                      )
                    }
                  </MediaModal>
                )}
              />
            </div>
          </Form.Group>
          <Form.Group controlId="description">
            <Form.Label className="mark-required">{t("posts_create_description")}</Form.Label>
            <Controller
              control={control}
              name="description"
              render={({field}) => <TextEdit {...field} />}
            />
            {errors.description?.message && (
              <Form.Text className="text-danger">
                {errors.description.message}
              </Form.Text>
            )}
          </Form.Group>

          <article id="seo" className="mt-4">
            <h3>{t("posts_create_seo")}</h3>

            <div className="mt-2 d-flex gap-2 flex-column">
              <Form.Group controlId="metaTitle">
                <Form.Label >{t("posts_create_metaTitle")}</Form.Label>
                <Form.Control
                  {...register("meta_title")}
                  placeholder={t("posts_create_metaTitle_plhd")}
                  type="text"
                />
                {errors.meta_title?.message && (
                  <Form.Text className="text-danger">
                    {errors.meta_title.message}
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group controlId="metaTags">
                <Form.Label >{t("posts_create_metaTags")}</Form.Label>
                <Controller
                  control={control}
                  name="meta_tag"
                  render={({field}) => (
                    <TagInput
                      onChange={field.onChange}
                      value={field.value}
                      placeholder={t("posts_create_metaTags_plhd")}
                      style={{borderColor: "#ced4da"}}
                      className="d-block"
                    />
                  )}
                />
                {(errors.meta_tag?.message || Array.isArray(errors.meta_tag)) && (
                  <Form.Text className="text-danger">
                    {errors?.meta_tag?.message ??
                      errors.meta_tag.find(Boolean).message}
                  </Form.Text>
                )}
              </Form.Group>


              <Form.Group controlId="metaDescription">
                <Form.Label >{t("posts_create_metaDescription")}</Form.Label>
                <Form.Control
                  {...register("meta_description")}
                  as="textarea"
                  rows={3}
                  placeholder={t("posts_create_metaDescription_plhd")}
                />
                {errors.meta_description?.message && (
                  <Form.Text className="text-danger">
                    {errors.meta_description.message}
                  </Form.Text>
                )}
              </Form.Group>
            </div>

          </article>


          <div className="d-flex justify-content-end">
            <Button className="px-4" type="submit">
              {t("posts_create_create")}
            </Button>
          </div>
        </Form>
      </section>
    </Layout>
  );
}
